import { useCallback, useReducer } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { GeneratedImagesService } from '../../../../../service/GeneratedImagesService';
import { GeneratedAudioService } from '../../../../../service/GeneratedAudioService';
import { GeneratedFileService } from '../../../../../service/GeneratedFileService';
import type { GeneratedImage } from '../../../../../types/generatedImage';
import type { GeneratedAudio } from '../../../../../types/generatedAudio';
import type { GeneratedFile } from '../../../../../types/generatedFile';

interface AssetModalState {
  imagePreview: { show: boolean; image: GeneratedImage | null };
  audioPreview: { show: boolean; audio: GeneratedAudio | null };
  filePreview: { show: boolean; file: GeneratedFile | null };
  renaming: {
    type: 'image' | 'audio' | 'file' | null;
    asset: GeneratedImage | GeneratedAudio | GeneratedFile | null;
  };
}

type AssetModalAction =
  | { type: 'PREVIEW_IMAGE'; payload: GeneratedImage }
  | { type: 'CLOSE_IMAGE_PREVIEW' }
  | { type: 'PREVIEW_AUDIO'; payload: GeneratedAudio }
  | { type: 'CLOSE_AUDIO_PREVIEW' }
  | { type: 'PREVIEW_FILE'; payload: GeneratedFile }
  | { type: 'CLOSE_FILE_PREVIEW' }
  | { type: 'RENAME_IMAGE'; payload: GeneratedImage }
  | { type: 'RENAME_AUDIO'; payload: GeneratedAudio }
  | { type: 'RENAME_FILE'; payload: GeneratedFile }
  | { type: 'CLOSE_RENAME' };

const initialAssetModalState: AssetModalState = {
  imagePreview: { show: false, image: null },
  audioPreview: { show: false, audio: null },
  filePreview: { show: false, file: null },
  renaming: { type: null, asset: null },
};

function assetModalReducer(
  state: AssetModalState,
  action: AssetModalAction,
): AssetModalState {
  switch (action.type) {
    case 'PREVIEW_IMAGE':
      return { ...state, imagePreview: { show: true, image: action.payload } };
    case 'CLOSE_IMAGE_PREVIEW':
      return { ...state, imagePreview: { show: false, image: null } };
    case 'PREVIEW_AUDIO':
      return { ...state, audioPreview: { show: true, audio: action.payload } };
    case 'CLOSE_AUDIO_PREVIEW':
      return { ...state, audioPreview: { show: false, audio: null } };
    case 'PREVIEW_FILE':
      return { ...state, filePreview: { show: true, file: action.payload } };
    case 'CLOSE_FILE_PREVIEW':
      return { ...state, filePreview: { show: false, file: null } };
    case 'RENAME_IMAGE':
      return { ...state, renaming: { type: 'image', asset: action.payload } };
    case 'RENAME_AUDIO':
      return { ...state, renaming: { type: 'audio', asset: action.payload } };
    case 'RENAME_FILE':
      return { ...state, renaming: { type: 'file', asset: action.payload } };
    case 'CLOSE_RENAME':
      return { ...state, renaming: { type: null, asset: null } };
  }
}

interface AssetMutations {
  deleteGeneratedImage: (id: string) => void;
  deleteGeneratedAudio: (id: string) => void;
  deleteGeneratedFile: (id: string) => void;
}

function useAssetHandlers(queryClient: QueryClient, mutations: AssetMutations) {
  const [modals, dispatch] = useReducer(
    assetModalReducer,
    initialAssetModalState,
  );

  const handleImagePreview = useCallback((image: GeneratedImage) => {
    dispatch({ type: 'PREVIEW_IMAGE', payload: image });
  }, []);

  const handleImageRename = useCallback((image: GeneratedImage) => {
    dispatch({ type: 'RENAME_IMAGE', payload: image });
  }, []);

  const handleImageRenameConfirm = useCallback(
    (newName: string) => {
      if (!modals.renaming.asset || modals.renaming.type !== 'image') return;
      GeneratedImagesService.rename(modals.renaming.asset.id, newName).then(
        () => {
          queryClient.invalidateQueries({ queryKey: ['generated-images'] });
        },
      );
      dispatch({ type: 'CLOSE_RENAME' });
    },
    [modals.renaming, queryClient],
  );

  const handleImageDelete = useCallback(
    (image: GeneratedImage) => {
      if (!window.confirm('Are you sure you want to delete this image?'))
        return;
      mutations.deleteGeneratedImage(image.id);
    },
    [mutations.deleteGeneratedImage],
  );

  const handleAudioPreview = useCallback((audio: GeneratedAudio) => {
    dispatch({ type: 'PREVIEW_AUDIO', payload: audio });
  }, []);

  const handleAudioRename = useCallback((audio: GeneratedAudio) => {
    dispatch({ type: 'RENAME_AUDIO', payload: audio });
  }, []);

  const handleAudioRenameConfirm = useCallback(
    (newName: string) => {
      if (!modals.renaming.asset || modals.renaming.type !== 'audio') return;
      GeneratedAudioService.rename(modals.renaming.asset.id, newName).then(
        () => {
          queryClient.invalidateQueries({ queryKey: ['generated-audio'] });
        },
      );
      dispatch({ type: 'CLOSE_RENAME' });
    },
    [modals.renaming, queryClient],
  );

  const handleAudioDelete = useCallback(
    (audio: GeneratedAudio) => {
      if (!window.confirm('Are you sure you want to delete this audio?'))
        return;
      mutations.deleteGeneratedAudio(audio.id);
    },
    [mutations.deleteGeneratedAudio],
  );

  const handleFilePreview = useCallback((file: GeneratedFile) => {
    dispatch({ type: 'PREVIEW_FILE', payload: file });
  }, []);

  const handleFileRename = useCallback((file: GeneratedFile) => {
    dispatch({ type: 'RENAME_FILE', payload: file });
  }, []);

  const handleFileRenameConfirm = useCallback(
    (newName: string) => {
      if (!modals.renaming.asset || modals.renaming.type !== 'file') return;
      GeneratedFileService.rename(modals.renaming.asset.id, newName).then(
        () => {
          queryClient.invalidateQueries({ queryKey: ['generated-files'] });
        },
      );
      dispatch({ type: 'CLOSE_RENAME' });
    },
    [modals.renaming, queryClient],
  );

  const handleFileDelete = useCallback(
    (file: GeneratedFile) => {
      if (!window.confirm('Are you sure you want to delete this file?')) return;
      mutations.deleteGeneratedFile(file.id);
    },
    [mutations.deleteGeneratedFile],
  );

  return {
    modals,
    dispatch,
    handlers: {
      onImagePreview: handleImagePreview,
      onImageRename: handleImageRename,
      onImageDelete: handleImageDelete,
      onAudioPreview: handleAudioPreview,
      onAudioRename: handleAudioRename,
      onAudioDelete: handleAudioDelete,
      onFilePreview: handleFilePreview,
      onFileRename: handleFileRename,
      onFileDelete: handleFileDelete,
    },
    renameModal: {
      show: modals.renaming.type !== null,
      type: modals.renaming.type,
      currentName: modals.renaming.asset?.name || '',
      onHide: () => dispatch({ type: 'CLOSE_RENAME' }),
      onRename:
        modals.renaming.type === 'image'
          ? handleImageRenameConfirm
          : modals.renaming.type === 'audio'
            ? handleAudioRenameConfirm
            : handleFileRenameConfirm,
    },
  };
}

export { useAssetHandlers };
export type { AssetModalState, AssetModalAction };
